import React, { PureComponent, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import Select, { components } from 'react-select';
import { AsyncPaginate } from 'react-select-async-paginate';
import Async from 'react-select/async';
import Creatable from 'react-select/creatable';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormLabel from "@material-ui/core/FormLabel";
import Typography from "@material-ui/core/Typography";
import isObject from 'lodash/isObject';

const styles = (theme) => ({
  select: {
    '&.inline': {
      display: 'flex',
      alignItems: 'center',
      marginTop: 10,
    },
    '& > div:first-child': {
      flex: 1,
    },
  },

  margin: {
    marginBottom: theme.spacing(3),
  },
  labelContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 2,
  },
  bootstrapFormLabel: {
    fontWeight: 400,
    color: theme.palette.text.primary,
    fontSize: 14,
    marginBottom: 8,
  },
  descriptionText: {

  },
  labelInline: {
    width: 120,
    textAlign: 'right',
    marginRight: 16,
    marginBottom: 0,
  },
  helpInline: {
    marginLeft: 136,
  },

});

const reactSelectStyles = {
  menuPortal: base => ({ ...base, zIndex: 9999 }),
  control: (base, state) => ({
    ...base,
    borderRadius: 0,
    backgroundColor: '#f8f8f8',
    borderColor: 'transparent',
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '12px 20px',
  }),
  multiValue: (base) => ({
    ...base,
  }),
  singleValue: (base) => ({
    ...base,
    color: '#696D75'
  }),
  dropdownIndicator: base => ({
    ...base,
    color: "#EB5E28" 
  })
};

class CustomSelect extends PureComponent {
  render() {
    const {
      style,
      classes,
      isMulti,
      customStyles,
      hasError,
      helpText,
      inline,
      async,
      disabled,
      transformValue,
      onChange,
      value,
      options,
      label,
      hint,
      noMargin,
      creatable,
      paginate,
      ...otherProps
    } = this.props;

    const AsyncComponent = paginate ? AsyncPaginate : Async;
    const Component = async ? AsyncComponent :  creatable ? Creatable : Select;
    const handleChange = (value) => {
      if (transformValue && value) {
        if (Array.isArray(value)) {
          onChange(value.map(i => i.value));
        } else {
          onChange(value.value)
        }
      } else {
        onChange(value);
      }
    };

    const getValue = () => {
      if (transformValue && value) {
        if (Array.isArray(value)){
          return value.map(i => {
            if (isObject(i)) return i;
            const option = options.find(o => o.value === i);
            return option || i;
          });
        }
        let option = value;
        options.find(i => {
            if (i.options) {
              const isMatch = i.options.find(i => i.value === value);
              if (isMatch) {
                option = {
                  ...isMatch,
                  label: `${i.label} - ${isMatch.label}`,
                };
              }
              return isMatch;
            }
            const isMatch = i.value === value;
            if (isMatch) {
              option = i;
            }
            return isMatch;
        });
        return option;
      } else {
        return value;
      }
    };

    return (
      <FormControl
        error={hasError}
        fullWidth
        style={style}
        className={cx(!noMargin && classes.margin)}
      >
        {label && (
          <FormLabel component="label" className={classes.bootstrapFormLabel}>
            {label}
          </FormLabel>
        )}
        {hint && (
          <Typography
            className={classes.hint}
            color="textSecondary"
            variant="caption"
          >
            {hint}
          </Typography>
        )}
        <Component
          closeMenuOnScroll={false}
          menuPlacement="auto"
          menuPortalTarget={document.body}
          components={{
            IndicatorSeparator: () => null,
          }}
          closeMenuOnSelect={!isMulti}
          className={cx('select', classes.select, { inline: inline })}
          isMulti={isMulti}
          isDisabled={disabled}
          styles={reactSelectStyles}
          hideSelectedOptions={false}
          onChange={handleChange}
          value={getValue()}
          options={options}
          isSearchable
          {...otherProps}
        />
        {helpText && (
          <FormHelperText
            className={inline && classes.helpInline}
            error={hasError}
          >
            {helpText}
          </FormHelperText>
        )}
      </FormControl>
    );
  }
}

CustomSelect.propTypes = {
  classes: PropTypes.object.isRequired,
  options: PropTypes.array,
  isMulti: PropTypes.bool,
  showClearAll: PropTypes.bool,
  customStyles: PropTypes.object,
  hasError: PropTypes.bool,
  helpText: PropTypes.string,
  inline: PropTypes.bool,
  async: PropTypes.bool,
  transformValue: PropTypes.bool,
  creatable: PropTypes.bool,
  loadOptions: PropTypes.func,
  label: PropTypes.string,
};

CustomSelect.defaultProps = {
  options: [],
  isMulti: true,
  showClearAll: true,
  customStyles: {},
};

const StyledSelect =  withStyles(styles)(CustomSelect);

export default StyledSelect;

const createOption = (label) => ({
  label,
  value: label,
});

export const TagsInput = ({ value, onChange, validate, ...other }) => {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState();

  const onInputChange = (val) => {
      setInputValue(val);
      if (error) {
        setError();
      }
  };

  const onKeyDown = (event) => {
    if (!inputValue) return;
    switch (event.key) {
      case 'Enter':
      case 'Tab':
      case 'Blur':
        event.preventDefault();
        if (value.find(i => i.value === inputValue)) return;
        if (validate) {
          validate(null, inputValue, (err) => {
            setError(err);
            if (!err) {
              onChange([...value, createOption(inputValue)]);
              setInputValue('');
            }
          });
        } else {
          onChange([...value, createOption(inputValue)]);
          setInputValue('');
        }
    }
  };

  const handleBlur =(e) => {
    e.key = 'Blur';
    onKeyDown(e);
  }

  const handleChange = (val) => {
    if (!val) {
      onChange([]);
    } else {
      onChange(val);
    }
  }

  return (
    <StyledSelect
      creatable
      isMulti
      isClearable
      menuIsOpen={false}
      value={value}
      inputValue={inputValue}
      onKeyDown={onKeyDown}
      onInputChange={onInputChange}
      onChange={handleChange}
      components={{
        DropdownIndicator: null
      }}
      onBlur={handleBlur}
      hasError={!!error}
      helpText={error}
      {...other}
    />
  )
}